import * as React from 'react';
import { observer } from 'mobx-react';
import { action, computed, observable, makeObservable, runInAction } from 'mobx';
import { TextField, ITextFieldProps } from '../TextBox/TextBox';
import { getBoundValue, setBoundValue } from '../Inputs/BoundModelProperty';

const errorMessage = 'The value must be a number';
const notWholeNumberErrorMessage = 'The value must be a whole number';

type numberModel = {
	number?: string;
};

/**
 * `modelProperty` is inherited from `ITextFieldProps` as `BoundProp<T, string | number>`
 * rather than narrowed to `number`: `onClickToClear` below writes `''` over the bound
 * property, so a purely numeric constraint would not describe what this component does.
 */
export interface INumberTextFieldProps<T> extends ITextFieldProps<T> {
	acceptWholeNumbersOnly?: boolean;
	formatBefore?: string;
	// Whether or not we want validation errors to appear
	showErrors?: boolean;
}

/**
 * A text field that will cast the value typed into it to a number and will not allow non numbers to be typed into it
 */
export const NumberTextField = observer(
	class NumberTextField<T> extends React.Component<INumberTextFieldProps<T>> {
		/**
		 * An internal model that contains the string value of the number that is stored in the real model
		 * This allows for values such as '1.' to be displayed even though it is really just '1'
		 */
		private model: numberModel = { number: undefined };

		/**
		 * The string value of the text field so the display value does not get mutated unexpectedly
		 */
		private stringValue: string = '';

		/**
		 * An error message to display in the case of an invalid number
		 */
		private error?: string = undefined;

		constructor(props: INumberTextFieldProps<T>) {
			super(props);

			makeObservable<NumberTextField<T>, 'model' | 'stringValue' | 'error' | 'allErrors'>(this, {
				model: observable,
				stringValue: observable,
				error: observable,
				allErrors: computed,
			});
		}

		/**
		 * A combination of any provided errors and the number error
		 */
		private get allErrors() {
			const { errors, showErrors } = this.props;

			if (!showErrors) {
				return [];
			}
			if (errors) {
				if (Array.isArray(errors)) {
					return this.error ? [this.error, ...errors] : errors;
				}
				return this.error ? [this.error, errors] : errors;
			}
			return this.error;
		}

		public render() {
			// `model`/`modelProperty` are pulled out of the spread so that each branch below can
			// supply its own pair — the internal `numberModel` and the caller's model have
			// different types, so they cannot be merged into one object and spread together.
			const { modelProperty, model, inputProps, ...rest } = this.props;

			const modelValue = getBoundValue(model, modelProperty);
			const stringValue = this.model.number;

			const sharedProps = {
				...rest,
				onClickToClear: this.onClickToClear,
				errors: this.allErrors,
				inputProps: {
					onChange: this.onChange,
					...inputProps,
				},
			};

			if (modelValue === Number(stringValue) || (modelValue === undefined && stringValue === '-')) {
				return <TextField {...sharedProps} model={this.model} modelProperty="number" />;
			}

			return <TextField {...sharedProps} model={model} modelProperty={modelProperty} />;
		}

		private onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
			const textValue = event.target.value.trim();
			const numberValue = Number(textValue);

			runInAction(() => {
				// Check if the input is actually a number
				if (Number.isNaN(numberValue) && textValue !== '-') {
					this.error = errorMessage;
					return;
				}

				// If this input only accepts whole numbers, check that next
				if (this.props.acceptWholeNumbersOnly && textValue.includes('.')) {
					this.error = notWholeNumberErrorMessage;
					return;
				}

				// Clear errors since we know we have a real number
				this.error = undefined;

				// Assign the correct text value
				if (textValue === '' || textValue === '-') {
					setBoundValue(this.props.model, this.props.modelProperty, undefined);
				} else {
					setBoundValue(this.props.model, this.props.modelProperty, numberValue);
				}
				this.model.number = textValue;
			});

			if (this.props.onAfterChange) {
				this.props.onAfterChange(event);
			}
		};

		private onClickToClear = (event: React.MouseEvent<HTMLButtonElement>) => {
			if (this.props.onClickToClear) {
				return this.props.onClickToClear(event);
			}
			runInAction(() => {
				setBoundValue(this.props.model, this.props.modelProperty, '');
			});
		};
	},
);
